import { Flex, Text } from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartTooltip } from "./PieChartToolTip";
import { t } from "i18next";
import { CustomLabel } from "./CustomLabel";
import { PieChartItem, pieChartProps } from "./shared";
import { observer } from "mobx-react-lite";
import { RiskFactor, Score } from "@keita/lanepatrol-sdk";
import { useCraScorePieCharts } from "../../hooks/useCraScorePieCharts";
import { tRiskFactor } from "../../../../coding/utils/score-utils";
import { useStores } from "../../../../shared/stores/RootStore";
import { useHlaScorePieCharts } from "../../hooks/useHlaScorePieCharts";
import { useMemo } from "react";

const ScorePieChartBase = ({ crashType }: { crashType: keyof Score }) => {
  const { filterStore } = useStores();
  const craData = useCraScorePieCharts(
    filterStore.filteredCraDistance,
    filterStore.filteredNodes,
  );

  const hlaData = useHlaScorePieCharts();

  const data = useMemo(() => {
    // no need to mix if only one type exists (or none exists)
    if (craData == null) return hlaData;
    if (hlaData == null) return craData;

    const data: Record<keyof Score, PieChartItem[]> = {
      global: [],
      vehicleToBicycle: [],
      bicycleToBicycle: [],
      bicycleToPedestrian: [],
      singleBicycle: [],
    };

    Object.keys(data).forEach((crashType) => {
      const hlaItems = hlaData[crashType as keyof Score];
      const craItems = craData[crashType as keyof Score];

      const joinedItems = [...hlaItems];

      for (const item of craItems) {
        const existingItem = joinedItems.find((i) => i.name === item.name);

        if (existingItem) {
          existingItem.value += item.value;
        } else {
          joinedItems.push(item);
        }
      }

      data[crashType as keyof Score] = joinedItems;
    });

    return data;
  }, [craData, hlaData]);

  if (!data)
    return (
      <Text fontSize="xs" alignSelf={"start"}>
        {t(`map:pieChart.noData`)}
      </Text>
    );

  const clockwiseData = [...data[crashType]].reverse();

  return (
    <Flex flexDirection="column" alignItems="center">
      <PieChart {...pieChartProps}>
        <Tooltip
          content={
            <PieChartTooltip translate={(s) => tRiskFactor(s as RiskFactor)} />
          }
        />

        <Pie
          nameKey="name"
          dataKey="value"
          label={CustomLabel}
          data={clockwiseData}
          outerRadius={72}
        >
          {clockwiseData.map((entry, i) => (
            <Cell key={i} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
    </Flex>
  );
};

export const ScorePieChart = observer(ScorePieChartBase);
